import {css, StyleSheet} from 'aphrodite';
import React from 'react';
import _ from 'underscore';

import {AMINO_ACID_TLA} from 'biojs/Codons';
import {
  PRECISION_ABSORBANCE,
  PRECISION_EXTINCTION_COEFFICIENT,
  PRECISION_INSTABILITY_INDEX,
  PRECISION_ISOELECTRIC_POINT,
  getMolecularWeightPrecision,
} from 'molbio/aa-sequences/util/AaSequenceConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import Tooltip from 'ui-library/bs/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import * as ProteinProperties from './util/AaSequenceProperties';
import getAminoAcidFrequency from './util/getAminoAcidFrequency';

const AMINO_ACID_ORDERING_FOR_FRQUENCY = 'ARNDCQEGHILKMFPSTWYVOU';

function AminoAcidFrequencyTable({aminoAcids}: {aminoAcids: string}): JSX.Element {
  const aminoAcidToCount = getAminoAcidFrequency(aminoAcids);
  const numAminoAcids = aminoAcids.length;

  // eslint-disable-next-line no-restricted-syntax
  const aminoAcidFrequencyTRs = _.map(
    AMINO_ACID_ORDERING_FOR_FRQUENCY,
    (aminoAcid: keyof typeof AMINO_ACID_TLA) => {
      const threeLetterAbbrev = AMINO_ACID_TLA[aminoAcid];
      const count = aminoAcidToCount.get(aminoAcid) as number;
      let percentage;
      if (numAminoAcids > 0) {
        percentage = (count / numAminoAcids) * 100;
      } else {
        percentage = 0;
      }
      return (
        <tr key={`aminoAcid-${aminoAcid}`}>
          <td {...getTestProps({component: 'AminoAcidProperties', element: 'abbreviation'})}>
            {threeLetterAbbrev}
          </td>
          <td {...getTestProps({component: 'AminoAcidProperties', element: 'aminoAcid'})}>{aminoAcid}</td>
          <td
            className="text-right mono"
            {...getTestProps({component: 'AminoAcidProperties', element: 'acidCount'})}
          >
            {count}
          </td>
          <td className="text-right mono">{percentage.toFixed(1)}%</td>
        </tr>
      );
    }
  );

  return (
    <div className={css(styles.tableWrapper)}>
      <h4>Amino Acid Frequencies</h4>
      <table className="table table-striped">
        <thead>
          <tr>
            <th colSpan={2}>Amino acid</th>
            <th colSpan={2}>Count</th>
          </tr>
        </thead>
        <tbody>{aminoAcidFrequencyTRs}</tbody>
      </table>
    </div>
  );
}

function ChargeDistributionTable({aminoAcids}: {aminoAcids: string}): JSX.Element {
  const phLevels = [4.0, 4.5, 5.0, 5.5, 6.0, 6.5, 7.0, 7.5, 8.0, 8.5, 9.0, 9.5, 10.0];
  return (
    <table className="table table-striped">
      <thead>
        <tr>
          <th>pH</th>
          <th>Charge</th>
        </tr>
      </thead>
      <tbody>
        {phLevels.map((phLevel) => (
          <tr key={`ph-${phLevel}`}>
            <td>{phLevel}</td>
            <td>{ProteinProperties.getChargeAtPH(aminoAcids, phLevel).toFixed(2)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

interface AminoAcidPropertiesProps {
  aminoAcids: string;
  // An optional react element that will be placed above the description list.
  alertMessage?: JSX.Element;
  // Optional additional properties to show at the top of the list.
  extraProperties?: Array<{name: string; value: string; dataTestAttrs?: DataTestAttrs}>;
}

/**
 * Component displaying information about a sequence of amino acids.
 *
 * This component needs to be a bit flexible since it's used in a few cases:
 * - "Analyze as Translation" when selecting a range of a sequence. This view
 *   provides information about the position in the range.
 * - "More stats" when selecting a range of a protein. This view also provides
 *   information about the position in the range.
 * - "Analyze Proteins" from the metadata tab, which views information on a
 *   full protein complex.
 */
function AminoAcidProperties(props: AminoAcidPropertiesProps): JSX.Element {
  const {aminoAcids, alertMessage, extraProperties} = props;
  const showIfNonempty = (el: JSX.Element): React.ReactNode => (props.aminoAcids.length > 0 ? el : 'N/A');
  return (
    <div className={css(styles.propertiesWrapper)}>
      {alertMessage}
      <div className={css(styles.tableWrapper)}>
        <h4>Properties</h4>
        <table className="table table-striped">
          <tbody>
            {extraProperties &&
              extraProperties.map(({name, value, dataTestAttrs}) => (
                <tr key={name}>
                  <td className={css(styles.primaryProperty)}>{name}</td>
                  <td className="mono" {...getTestProps(dataTestAttrs)}>
                    {value}
                  </td>
                </tr>
              ))}
            <tr>
              <td className={css(styles.primaryProperty)}>
                Molecular weight
                <Tooltip title="Cys fully reduced">
                  <BenchlingIcon className={css(styles.helper)} icon="circle-question" />
                </Tooltip>
              </td>
              <td className="molecularWeight mono">
                {showIfNonempty(
                  <span>
                    {ProteinProperties.getMolecularWeight(aminoAcids).toFixed(getMolecularWeightPrecision())}{' '}
                    Da
                  </span>
                )}
              </td>
            </tr>
            <tr>
              <td className={css(styles.primaryProperty)}>Isoelectric point (pI)</td>
              <td className="mono">
                {showIfNonempty(
                  <span>
                    {ProteinProperties.getIsoelectricPoint(aminoAcids).toFixed(PRECISION_ISOELECTRIC_POINT)}
                  </span>
                )}
              </td>
            </tr>
            <tr>
              <td className={css(styles.primaryProperty)}>Extinction coefficient</td>
              <td />
            </tr>
            <tr>
              <td className={css(styles.secondaryProperty)}>Cys fully reduced</td>
              <td className="mono">
                {showIfNonempty(
                  <span>
                    {ProteinProperties.getExtinctionCoefficient(aminoAcids, {
                      assumeCysReduced: true,
                    }).toFixed(PRECISION_EXTINCTION_COEFFICIENT)}{' '}
                    M<sup>-1</sup>cm<sup>-1</sup>
                  </span>
                )}
              </td>
            </tr>
            <tr>
              <td className={css(styles.secondaryProperty)}>Abs 0.1% (1 g/l)</td>
              <td className="mono">
                {showIfNonempty(
                  <span>
                    {ProteinProperties.getAbsorbance(aminoAcids, {assumeCysReduced: true}).toFixed(
                      PRECISION_ABSORBANCE
                    )}
                  </span>
                )}
              </td>
            </tr>
            <tr>
              <td className={css(styles.secondaryProperty)}>Cys fully oxidized</td>
              <td className="mono">
                {showIfNonempty(
                  <span>
                    {ProteinProperties.getExtinctionCoefficient(aminoAcids, {
                      assumeCysReduced: false,
                    }).toFixed(PRECISION_EXTINCTION_COEFFICIENT)}{' '}
                    M<sup>-1</sup>cm<sup>-1</sup>
                  </span>
                )}
              </td>
            </tr>
            <tr>
              <td className={css(styles.secondaryProperty)}>Abs 0.1% (1 g/l)</td>
              <td className="mono">
                {showIfNonempty(
                  <span>
                    {ProteinProperties.getAbsorbance(aminoAcids, {assumeCysReduced: false}).toFixed(
                      PRECISION_ABSORBANCE
                    )}
                  </span>
                )}
              </td>
            </tr>
            <tr>
              <td className={css(styles.primaryProperty)}>Instability index</td>
              <td className="mono">
                {showIfNonempty(
                  <span>
                    {ProteinProperties.getInstabilityIndex(aminoAcids).toFixed(PRECISION_INSTABILITY_INDEX)}
                    {ProteinProperties.getInstabilityIndex(aminoAcids) <
                    ProteinProperties.INSTABILITY_INDEX_THRESHOLD
                      ? ' (stable)'
                      : ' (unstable)'}
                  </span>
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <AminoAcidFrequencyTable aminoAcids={aminoAcids} />

      {props.aminoAcids.length > 0 && (
        <div className={css(styles.tableWrapper)}>
          <h4>Net Charge</h4>
          <ChargeDistributionTable aminoAcids={aminoAcids} />
        </div>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  propertiesWrapper: {
    margin: 0,
    width: '50%',
    minWidth: '290px',
  },
  tableWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  secondaryProperty: {
    paddingLeft: '30px',
  },
  primaryProperty: {
    fontWeight: 'bold',
  },
  helper: {
    paddingLeft: '6px',
    paddingTop: '2px',
  },
});

export default AminoAcidProperties;
